// Analytics loader and event tracker.
// Loads GA4 + Meta Pixel + Microsoft Clarity only after the user has given the
// corresponding consent (analytics for GA/Clarity, marketing for Pixel/Google
// Ads). Provides a uniform `track()` helper for fire-and-forget events that
// respects consent.

import type { AnalyticsConfig } from '@/types/marketing'
import { readConsent, onConsentChange } from './consent'
import { postMetaEventPing } from './metaEventPing'

/** Clarity's global: a callable that queues args until the real tag loads. */
interface ClarityFn {
  (...args: unknown[]): void
  q?: unknown[]
}

declare global {
  interface Window {
    // Declared to MATCH @rotorvideos/utilities' global augmentation exactly.
    // TypeScript requires duplicate Window property declarations to be
    // identical (TS2717), and the Rotor SDK augments these two. Diverging by a
    // single type argument fails the build with an error that names neither
    // package.
    dataLayer?: Array<Record<string, any>>
    gtag?: (event: string, action: string, params?: Record<string, any>) => void
    fbq?: (...args: unknown[]) => void
    _fbq?: unknown
    clarity?: ClarityFn
  }
}

let loadedGA = false
let loadedPixel = false
let loadedClarity = false
let installed = false
let activeConfig: AnalyticsConfig | null = null

// Meta's standard events. Any other name must be sent via fbq('trackCustom', …);
// sending it via fbq('track', …) logs "You are sending a non-standard event"
// and Meta does not record it as a proper custom conversion.
// https://developers.facebook.com/docs/meta-pixel/reference#standard-events
const META_STANDARD_EVENTS = new Set([
  'AddPaymentInfo', 'AddToCart', 'AddToWishlist', 'CompleteRegistration',
  'Contact', 'CustomizeProduct', 'Donate', 'FindLocation', 'InitiateCheckout',
  'Lead', 'PageView', 'Purchase', 'Schedule', 'Search', 'StartTrial',
  'SubmitApplication', 'Subscribe', 'ViewContent',
])

function installScript(src: string): void {
  if (typeof document === 'undefined') return
  const s = document.createElement('script')
  s.async = true
  s.src = src
  document.head.appendChild(s)
}

function ensureGA(measurementId: string): void {
  if (loadedGA) return
  loadedGA = true
  installScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`)
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args as unknown as Record<string, any>)
  } as typeof window.gtag
  // gtag('js', <Date>) is the documented bootstrap call and the one place the
  // real API does not match the (event, action, params) signature above.
  const gtag = window.gtag as unknown as (...a: unknown[]) => void
  gtag('js', new Date())
  gtag('config', measurementId, { anonymize_ip: true })
}

/**
 * Fire one Meta Pixel PageView carrying a fresh dedup id, and post the matching
 * server-side beacon so the browser and CAPI copies dedupe. Callers must already
 * be inside a `consent.marketing && window.fbq` branch — `postMetaEventPing` also
 * self-checks consent (defense in depth).
 */
function firePageViewPixel(): void {
  const eventId = generateEventId()
  window.fbq!('track', 'PageView', {}, { eventID: eventId })
  postMetaEventPing('PageView', eventId)
}

function ensurePixel(pixelId: string): void {
  if (loadedPixel) return
  loadedPixel = true
  // Standard Meta Pixel snippet, inlined.
  /* eslint-disable */
  ;(function (f: any, b: any, e: string, v: string) {
    if (f.fbq) return
    const n: any = f.fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args)
    }
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    const t = b.createElement(e); t.async = true; t.src = v
    const s = b.getElementsByTagName(e)[0]
    s.parentNode.insertBefore(t, s)
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
  /* eslint-enable */
  window.fbq!('init', pixelId)
  firePageViewPixel()
}

/**
 * Load Microsoft Clarity (session replay + heatmaps) for `projectId`.
 * Gated on `analytics` consent by the caller — Clarity records interactions,
 * so it must never run before the visitor accepts analytics cookies.
 */
function ensureClarity(projectId: string): void {
  if (loadedClarity) return
  loadedClarity = true
  // Standard Clarity snippet, inlined. The queueing stub is defined *before*
  // the tag is requested so a consent signal fired in the same tick is
  // replayed once the real implementation loads.
  window.clarity =
    window.clarity ||
    function clarity(...args: unknown[]) {
      ;(window.clarity!.q = window.clarity!.q || []).push(args)
    }
  installScript(`https://www.clarity.ms/tag/${projectId}`)
}

function applyConsent(): void {
  if (!activeConfig) return
  const consent = readConsent()
  if (consent.analytics && activeConfig.ga4_measurement_id) {
    ensureGA(activeConfig.ga4_measurement_id)
  }
  if (consent.marketing && activeConfig.meta_pixel_id) {
    ensurePixel(activeConfig.meta_pixel_id)
  }
  if (consent.analytics && activeConfig.clarity_project_id) {
    ensureClarity(activeConfig.clarity_project_id)
  }
  // A loaded script can't be unloaded, so on withdrawal we tell the SDKs to
  // stop via their consent APIs. Runs on every consent change (installAnalytics
  // subscribes applyConsent to onConsentChange), so grant <-> withdraw both apply.
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
    })
  }
  if (window.fbq) {
    window.fbq('consent', consent.marketing ? 'grant' : 'revoke')
  }
  if (window.clarity) {
    // NB: Clarity spells these with a capital S (ad_Storage), unlike gtag's
    // all-lowercase ad_storage two blocks up — do not "normalise" them.
    // 'consentv2' supersedes the deprecated clarity('consent', bool) form.
    // https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-consent-api-v2
    window.clarity('consentv2', {
      ad_Storage: consent.marketing ? 'granted' : 'denied',
      analytics_Storage: consent.analytics ? 'granted' : 'denied',
    })
  }
}

/**
 * Wire the analytics loader. Idempotent — safe to call on every page render.
 *
 * A page that ships NO config (null — the sign-in pages: /log-in,
 * /account-hub/log-in, /verify-otp) clears the active one, so accepting
 * cookies on that page loads nothing. A tag an earlier page already loaded
 * cannot be unloaded; `analyticsNavigation` below handles that case.
 */
export function installAnalytics(config: AnalyticsConfig | null | undefined): void {
  if (!config) {
    activeConfig = null
    return
  }
  activeConfig = config
  applyConsent()
  if (installed) return
  installed = true
  onConsentChange(() => applyConsent())
}

/** True once any third-party tag (GA4, Pixel, Clarity) is in this document. */
export function analyticsLoaded(): boolean {
  return loadedGA || loadedPixel || loadedClarity
}

/**
 * What an Inertia navigation should do about analytics, given the config the
 * new page shipped. A page with none (the sign-in pages) is never tracked;
 * and if this document already loaded a tag — the visitor reached the page
 * by an Inertia visit or Back from a marketing page — it is reloaded, because
 * a loaded tag keeps recording whatever page it finds itself on.
 */
export function analyticsNavigation(config: AnalyticsConfig | null | undefined): 'track' | 'skip' | 'reload' {
  if (config) return 'track'
  return analyticsLoaded() ? 'reload' : 'skip'
}

interface TrackOptions {
  /** Google Ads conversion label key (matches conversion_labels in config) */
  conversion?: 'application_submitted' | 'application_approved' | 'payment_success'
  /**
   * Meta Pixel event name(s); defaults to inferred from `name`. An array fires
   * one `fbq()` call per name, all sharing the same `data` and `eventId` so the
   * browser and server copies of each name dedup independently (e.g. a submit
   * emitting both `Lead` and `SubmitApplication`).
   */
  metaEvent?: string | string[]
  /** Additional payload passed through to GA + Pixel */
  data?: Record<string, unknown>
  /** Dedup id; generated when omitted. Echo the return value to the server. */
  eventId?: string
}

/** Generate a dedup event id (UUID where available, timestamp fallback otherwise). */
export function generateEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/**
 * Fire a GA4 + Meta Pixel pageview for the current location, respecting consent.
 * Used for SPA route changes — the initial page load is already counted by the
 * `gtag('config')` call and the Pixel's `init` PageView, so callers must avoid
 * firing this for the first render (see the entrypoint's path-change guard).
 */
export function trackPageView(): void {
  if (typeof window === 'undefined') return
  const consent = readConsent()
  if (consent.analytics && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: window.location.pathname + window.location.search,
      page_location: window.location.href,
      page_title: typeof document !== 'undefined' ? document.title : undefined,
    })
  }
  if (consent.marketing && window.fbq) {
    firePageViewPixel()
  }
}

/**
 * Fire an analytics event respecting consent. Falls back silently if blocked.
 * Returns the `event_id` used — pass it to the server (ConversionPinger event_id:)
 * so GA4 + Meta dedupe the browser and server events for the same conversion.
 */
export function track(name: string, opts: TrackOptions = {}): string {
  const consent = readConsent()
  const eventId = opts.eventId ?? generateEventId()
  const data = { ...(opts.data ?? {}), event_id: eventId }
  if (consent.analytics && window.gtag) {
    window.gtag('event', name, data)
  }
  if (consent.marketing) {
    if (window.fbq) {
      const metaNames = Array.isArray(opts.metaEvent)
        ? opts.metaEvent
        : [opts.metaEvent ?? name]
      for (const metaName of metaNames) {
        const fbqMethod = META_STANDARD_EVENTS.has(metaName) ? 'track' : 'trackCustom'
        window.fbq(fbqMethod, metaName, opts.data ?? {}, { eventID: eventId })
      }
    }
    if (window.gtag && opts.conversion && activeConfig?.google_ads_conversion_id) {
      const label = activeConfig.conversion_labels?.[opts.conversion]
      if (label) {
        window.gtag('event', 'conversion', {
          send_to: `${activeConfig.google_ads_conversion_id}/${label}`,
          ...data,
        })
      }
    }
  }
  return eventId
}
